<template>
  <Breadcrumbs is-collection-page />

  <div v-if="collection.image" class="collection__image-wrapper container">
    <Picture :images="image" class="collection__image" />
  </div>

  <div
    class="collection__header"
    :class="{ 'collection__header--loading': loadingMore }"
  >
    <div class="collection__info">
      <h1 class="collection__title">{{ collection.title }}</h1>
      <p class="collection__description">{{ collection.description }}</p>
    </div>

    <ProductTypes
      v-if="shouldShowProductTypes && !mq.mdPlus"
      :types="types"
      :selected-filters="filters"
      @change="handleTypeChange"
    />
  </div>

  <div class="collection__bar">
    <ProductTypes
      v-if="shouldShowProductTypes && mq.mdPlus"
      :types="types"
      :selected-filters="filters"
      @change="handleTypeChange"
    />
    <div class="toolbar__wrapper">
      <div class="toolbar">
        <div class="toolbar__item">
          <button class="toolbar__button" @click="setIsFiltersOpen(true)">
            Filter
          </button>
        </div>
        <div v-if="!mq.mdPlus" class="toolbar__item">
          <label class="radio-text-toggle">
            <input v-model="layout" type="radio" value="list" name="layout" />
            <span>List</span>
          </label>
          <label class="radio-text-toggle">
            <input v-model="layout" type="radio" value="grid" name="layout" />
            <span>Grid</span>
          </label>
        </div>
      </div>
    </div>
  </div>

  <ProductGrid
    v-if="shouldShowProductsGrid"
    :products="products"
    :layout="layout"
    :loading="loadingMore"
    :flat-lays="flatLays.toLowerCase?.() === 'true'"
  />

  <NoProducts v-else />

  <CallToAction
    v-if="showCallToAction"
    heading="MEMBERS ONLY"
    text="Sign up and create a Members Only account to receive £20 credit towards your first order, enjoy rewards and benefits, and be the first to know about all things Rat & Boa."
  >
    <template #cta>
      <Button class="call-to-action-button" tag="a" href="/pages/members">
        Learn More
      </Button>
    </template>
  </CallToAction>

  <ScrollTrigger
    v-if="products.length > 0 && !loadingMore"
    @intersected="fetchMore"
  ></ScrollTrigger>

  <Filters
    :available-filters="collection.products.filters"
    :selected-filters="filters"
    @filterChange="handleFilterChange"
    @sortChange="handleSortChange"
  />

  <DataCollectionTrigger />
</template>

<script>
import isEmpty from "lodash-es/isEmpty";
import { mapActions, mapGetters } from "vuex";

import ProductGrid from "@/components/collection/ProductGrid.vue";
import NoProducts from "@/components/collection/NoProducts.vue";
import ScrollTrigger from "@/components/ScrollTrigger.vue";
import collectionQuery from "@/graphql/queries/Collection.gql";
import { viewCollectionEvent } from "@/helpers/events.js";
import { unwrapNodes } from "@/helpers/graphql";
import { PER_PAGE, RelayConnection } from "@/helpers/pagination";
import Picture from "@/components/Picture.vue";
import Filters from "@/components/collection/Filters.vue";
import ProductTypes from "@/components/collection/ProductTypes.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import CallToAction from "@/components/CallToAction.vue";
import Button from "@/components/Button.vue";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import DataCollectionTrigger from "@/components/DataCollectionTrigger.vue";

export default {
  name: "CollectionPage",
  components: {
    Filters,
    ProductGrid,
    ProductTypes,
    ScrollTrigger,
    NoProducts,
    Picture,
    Breadcrumbs,
    CallToAction,
    Button,
    DataCollectionTrigger,
  },
  inject: ["mq"],
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
    flatLays: {
      type: String,
      default: "false",
    },
  },
  data() {
    return {
      filters: [],
      collection: {
        products: new RelayConnection(),
      },
      photoMode: "model",
      openFilters: false,
      loadingMore: true,
      lastPaginationCursor: null,
    };
  },
  computed: {
    ...mapGetters("app", ["collectionPageLayout"]),
    shouldShowProductsGrid() {
      return this.loadingMore || this.products.length > 0;
    },
    layout: {
      get() {
        return this.collectionPageLayout;
      },
      set(value) {
        this.setUserPreferences({ key: "collectionPageLayout", value });
      },
    },
    products() {
      return unwrapNodes(this.collection.products?.edges);
    },
    image() {
      if (
        this.collection.image &&
        this.collection.image.altText === "Nosto icon"
      ) {
        return null;
      }
      return this.collection.image;
    },
    types() {
      const productTypes = this.collection.products?.filters?.find(
        (filter) => filter.id === "filter.p.product_type"
      );

      return productTypes?.values;
    },
    shouldShowProductTypes() {
      return this.types && this.types.length > 1;
    },
    showCallToAction() {
      const { enableCallToAction = false } = getFeatureToggles();
      return !this.mq.mdPlus && enableCallToAction;
    },
  },
  methods: {
    ...mapActions("app", ["setUserPreferences"]),
    ...mapActions("collection", [
      "setIsFiltersOpen",
      "setPriceRange",
      "setCollection",
    ]),
    unwrapNodes,
    // handleFetchMore() {
    //   setTimeout(() => {
    //     this.fetchMore();
    //   }, 100);
    // },
    async fetchMore() {
      if (!this.collection.products.pageInfo.hasNextPage) {
        return;
      }

      if (this.loadingMore) {
        return;
      }

      this.loadingMore = true;

      const edges = this.collection.products?.edges;
      const lastEdge = edges[edges.length - 1];
      const paginationCursor = lastEdge.cursor;

      if (this.lastPaginationCursor === paginationCursor) {
        console.log("Duplicated cursor");
        return;
      }

      try {
        await this.$apollo.queries.collection.fetchMore({
          variables: {
            after: paginationCursor,
          },
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingMore = false;
        this.lastPaginationCursor = paginationCursor;
      }
    },
    handleFilterChange(filters) {
      this.filters = filters;
      this.$apollo.queries.collection.refetch({
        product_filters: filters,
      });
    },
    handleTypeChange(filters) {
      const filterKey = Object.keys(filters)[0];
      const filtersWithoutTypes = this.filters.filter((filter) => {
        if (Object.keys(filter)[0] === filterKey) {
          return false;
        }
        return true;
      });

      // Only push the new filter if it has a value, this makes 'Shop All' work.
      if (Object.values(filters)[0]) {
        filtersWithoutTypes.push(filters);
      }

      this.filters = filtersWithoutTypes;

      this.$apollo.queries.collection.refetch({
        product_filters: filtersWithoutTypes,
      });
    },
    handleSortChange(sort) {
      this.$apollo.queries.collection.refetch({
        sort: sort.key,
        reverse: sort.reverse,
      });
    },
  },
  apollo: {
    collection: {
      query: collectionQuery,
      skip() {
        return !this.collectionHandle;
      },
      variables() {
        return {
          collection: this.collectionHandle,
          first: PER_PAGE,
        };
      },
      result(resp) {
        const data = resp.data;

        this.setCollection(data?.collection);

        viewCollectionEvent(data?.collection.title);

        if (isEmpty(this.filters)) {
          const filters = data?.collection.products.filters;
          if (filters) {
            const priceFilter = filters.find(
              (filter) => filter.id === "filter.v.price"
            );

            if (priceFilter) {
              const { price } = JSON.parse(priceFilter.values[0].input);
              this.setPriceRange(price);
            }
          }
        }

        this.loadingMore = false;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.collection__header {
  background-color: $color-light;
  padding: 60px 0px 20px 0px;

  @include respond-above(md) {
    padding: 80px 0;
  }

  &--loading {
    height: 300px;
    margin-bottom: 50px;
  }

  .types {
    padding-top: 15px;
  }
}

.collection__image-wrapper {
  @include respond-below(md) {
    padding-left: 0;
    padding-right: 0;
  }

  ::v-deep(.picture__wrapper--loading) {
    padding-bottom: 50%;
  }
}

.collection__info {
  @extend .container;

  @include respond-above(md) {
    padding: 0 0 0 40px;
  }
}

.collection__title {
  @include t__editorial-headline-10;

  @include respond-above(md) {
    @include t__editorial-headline-30;
  }
}

.collection__description {
  margin-top: 10px;
  @include t__editorial-sub-headline-10;

  @include respond-above(md) {
    @include t__editorial-sub-headline-20;
    max-width: 620px;
  }
}

.toolbar {
  background: $color-white;
  display: flex;
  justify-content: space-between;
  padding: 0 10px;

  @include respond-above(md) {
    justify-content: end;
    padding-right: 0;
    margin-right: -10px;
  }

  &__wrapper {
    position: relative;

    margin: 0 auto;
    max-width: 1920px;
    width: 100%;
    z-index: 1;

    @include respond-above(md) {
      position: static;
      flex: 0 1 auto;
      margin-left: 0;
    }
  }

  &__button {
    padding: 20px 10px;
    background: none;
    outline: none;
    border: none;
    @include t__utility-label-20;
  }

  label {
    display: inline-block;
    padding: 20px 10px;
    @include t__utility-label-20;

    input[type="radio"] {
      position: fixed;
      opacity: 0;
      pointer-events: none;

      &:checked + span {
        font-weight: bold;
      }
    }
  }
}

.radio-text-toggle {
  text-transform: uppercase;

  & + .radio-text-toggle {
    position: relative;
    &::before {
      position: absolute;
      content: "|";
      left: 0;
    }
  }

  span {
    cursor: pointer;
  }
}

.collection__bar {
  display: grid;
  grid-template-columns: 1fr auto;
  @extend .container;

  @include respond-above(md) {
    overflow-y: auto;
    padding: 0 20px;
  }
}

.call-to-action-button {
  background-color: $color-white;
  color: $color-dark;
  margin: 0 0 40px;
}
</style>
